import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Check, Sparkles, Users, Calendar, Building } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { MandalaCorner } from '../../components/luxury/MandalaCorner';
import { FloatingDiya } from '../../components/luxury/FloatingDiya';
import { api } from '../../App';
import { toast } from 'sonner';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const DONATION_AMOUNTS = [
  { key: '10', label: '£10', impact: 'Supports community events' },
  { key: '25', label: '£25', impact: 'Funds cultural programs' },
  { key: '50', label: '£50', impact: 'Preserves heritage activities' },
  { key: '100', label: '£100', impact: 'Major community initiative' },
];

const IMPACT_STATS = [
  { icon: Users, value: '500+', label: 'Families Served' },
  { icon: Calendar, value: '25+', label: 'Events Annually' },
  { icon: Building, value: '£50k+', label: 'Community Investment' },
];

export default function LuxuryDonations() {
  const [selectedAmount, setSelectedAmount] = useState('25');
  const [customAmount, setCustomAmount] = useState('');
  const [form, setForm] = useState({ donor_name: '', donor_email: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/api/checkout/donation', {
        amount_key: selectedAmount,
        custom_amount: selectedAmount === 'custom' ? parseFloat(customAmount) : null,
        donor_name: form.donor_name,
        donor_email: form.donor_email,
        origin_url: window.location.origin
      });
      if (res.url) window.location.href = res.url;
    } catch (err) {
      toast.error(err.message || 'Donation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(var(--lux-royal-soft))] to-[hsl(var(--lux-ivory))] overflow-hidden lux-bandhani-dots">
        <MandalaCorner position="top-left" className="opacity-20" />
        <FloatingDiya className="top-10 right-20 hidden lg:block" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Badge className="mb-6 bg-[hsl(var(--lux-gold-soft))] text-[hsl(var(--lux-royal))] border-[hsl(var(--lux-gold)/0.4)]">
              <Heart className="w-3 h-3 mr-1" fill="currentColor" /> Support Our Mission
            </Badge>
            <h1 className="text-5xl sm:text-6xl font-bold text-[hsl(var(--lux-royal))] mb-6" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="luxury-donations-title">
              Make a <span className="text-[hsl(var(--lux-gold))]">Difference</span>
            </h1>
            <p className="text-lg text-[hsl(var(--lux-ink)/0.8)] max-w-2xl mx-auto mb-4">
              Your generous contribution helps preserve and promote Gujarati heritage for future generations.
            </p>
            <p className="text-sm text-[hsl(var(--lux-ink)/0.6)]">
              Registered Charity No. <span className="font-semibold text-[hsl(var(--lux-royal))]">513936</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Donation Form — Choose Your Contribution (on top) */}
      <section className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Card className="lux-ornate-card">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-3 text-center" style={{ fontFamily: 'Cormorant Garamond' }}>
                  Choose Your Contribution
                </h2>
                <p className="text-sm text-[hsl(var(--lux-ink)/0.75)] text-center mb-8" data-testid="donation-welcome-message">
                  Any amount is welcome and deeply appreciated — every contribution, big or small, helps our community thrive.
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Amount Selection */}
                  <div>
                    <Label className="text-lg mb-4 block">Donation Amount</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      {DONATION_AMOUNTS.map(amt => (
                        <button
                          key={amt.key}
                          type="button"
                          onClick={() => setSelectedAmount(amt.key)}
                          className={`p-4 rounded-xl border-2 transition-all text-center ${
                            selectedAmount === amt.key
                              ? 'border-[hsl(var(--lux-gold))] bg-[hsl(var(--lux-gold-soft))]'
                              : 'border-[hsl(var(--lux-gold)/0.3)] hover:border-[hsl(var(--lux-gold)/0.6)]'
                          }`}
                          data-testid={`luxury-donation-amount-${amt.key}`}
                        >
                          <div className="text-2xl font-bold text-[hsl(var(--lux-royal))]" style={{ fontFamily: 'Cormorant Garamond' }}>
                            {amt.label}
                          </div>
                          <div className="text-xs text-[hsl(var(--lux-ink)/0.6)] mt-1">{amt.impact}</div>
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedAmount('custom')}
                      className={`w-full p-4 rounded-xl border-2 transition-all ${
                        selectedAmount === 'custom'
                          ? 'border-[hsl(var(--lux-gold))] bg-[hsl(var(--lux-gold-soft))]'
                          : 'border-[hsl(var(--lux-gold)/0.3)] hover:border-[hsl(var(--lux-gold)/0.6)]'
                      }`}
                      data-testid="luxury-donation-amount-custom"
                    >
                      <div className="text-lg font-semibold text-[hsl(var(--lux-royal))]">Custom Amount</div>
                    </button>
                    {selectedAmount === 'custom' && (
                      <div className="mt-4">
                        <Input
                          type="number"
                          placeholder="Enter amount in £"
                          value={customAmount}
                          onChange={e => setCustomAmount(e.target.value)}
                          min="1"
                          max="10000"
                          className="text-center text-xl"
                          data-testid="luxury-donation-custom-input"
                        />
                      </div>
                    )}
                  </div>

                  {/* Donor Info */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Your Name (Optional)</Label>
                      <Input
                        value={form.donor_name}
                        onChange={e => setForm({...form, donor_name: e.target.value})}
                        placeholder="Anonymous"
                        data-testid="luxury-donation-name-input"
                      />
                    </div>
                    <div>
                      <Label>Email (Optional)</Label>
                      <Input
                        type="email"
                        value={form.donor_email}
                        onChange={e => setForm({...form, donor_email: e.target.value})}
                        placeholder="your@email.com"
                        data-testid="luxury-donation-email-input"
                      />
                    </div>
                  </div>

                  {/* Trust Badges */}
                  <div className="flex flex-wrap items-center justify-center gap-4 py-4 border-t border-b border-[hsl(var(--lux-gold)/0.2)]">
                    <Badge variant="outline" className="border-[hsl(var(--lux-gold)/0.6)]">
                      <Check className="w-3 h-3 mr-1" /> Secure Payment
                    </Badge>
                    <Badge variant="outline" className="border-[hsl(var(--lux-gold)/0.6)]">
                      <Check className="w-3 h-3 mr-1" /> Tax Deductible
                    </Badge>
                    <Badge variant="outline" className="border-[hsl(var(--lux-gold)/0.6)]">
                      <Check className="w-3 h-3 mr-1" /> Charity No. 513936
                    </Badge>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || (selectedAmount === 'custom' && !customAmount)}
                    className="w-full bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))] h-14 text-lg shadow-[var(--lux-shadow-1)] hover:shadow-[var(--lux-shadow-2)]"
                    data-testid="luxury-donation-submit-button"
                  >
                    <Heart className="w-5 h-5 mr-2" fill="currentColor" />
                    {loading ? 'Processing...' : 'Donate Now'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Benefits / impact — description below the contribution section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-[hsl(var(--lux-royal))] text-center mb-8" style={{ fontFamily: 'Cormorant Garamond' }}>
            Where Your Contribution Goes
          </h3>
          <div className="grid sm:grid-cols-3 gap-8">
            {IMPACT_STATS.map((stat, idx) => (
              <motion.div key={idx} {...fadeInUp} transition={{ delay: idx * 0.1 }} className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center">
                  <stat.icon className="w-8 h-8 text-[hsl(var(--lux-royal))]" />
                </div>
                <div className="text-4xl font-bold text-[hsl(var(--lux-gold))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>
                  {stat.value}
                </div>
                <div className="text-sm text-[hsl(var(--lux-ink)/0.7)]">{stat.label}</div>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-sm text-[hsl(var(--lux-ink)/0.7)] mt-8 max-w-2xl mx-auto">
            Every donation — of any amount — is welcomed and appreciated. Your generosity funds cultural festivals,
            supports our elders and Saturday Club children, and keeps Gujarati heritage alive in Nottingham. Aabhar!
          </p>
        </div>
      </section>
    </div>
  );
}
